import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import { toast } from "sonner";
import { Trash2, X, MapPin, Pencil, LocateFixed } from "lucide-react";
import { useAuth } from "@/lib/auth";

const TYPE_LABEL = {
  construction: "Construction",
  dump_yard: "Dump Yard",
  fill_site: "Fill Site",
};
const TYPE_COLORS = {
  construction: "bg-orange-50 text-orange-800 border-orange-200",
  dump_yard: "bg-gray-100 text-gray-800 border-gray-300",
  fill_site: "bg-blue-50 text-blue-800 border-blue-200",
};

export default function Sites() {
  const { user } = useAuth();
  const [sites, setSites] = useState([]);
  const [editing, setEditing] = useState(null); // null | 'new' | site object
  const [backfilling, setBackfilling] = useState(false);

  const refresh = useCallback(async () => setSites((await api.get("/sites")).data), []);
  useEffect(() => { refresh(); }, [refresh]);

  const backfill = async () => {
    if (!window.confirm("Auto-fill lat/lon on every site that has an address but no coordinates? Takes ~1 second per site.")) return;
    setBackfilling(true);
    try {
      const { data } = await api.post("/sites/backfill-coords");
      const base = `Updated ${data.updated} · Skipped ${data.skipped} · Failed ${data.failed}`;
      if (data.aborted) {
        toast.warning(`${base} — aborted early (provider unreachable or rate-limited). Try again in a minute.`);
      } else {
        toast.success(base);
      }
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Backfill failed");
    } finally { setBackfilling(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this site?")) return;
    await api.delete(`/sites/${id}`);
    refresh();
  };

  return (
    <div>
      <PageHeader
        title="Sites"
        subtitle="Locations directory"
        actions={
          <div className="flex items-center gap-2">
            {user?.role === "admin" && (
              <button
                onClick={backfill}
                disabled={backfilling}
                data-testid="backfill-coords-button"
                className="flex items-center gap-1.5 border border-gray-300 text-gray-700 hover:bg-gray-50 text-xs font-semibold uppercase tracking-widest px-4 py-2.5 disabled:opacity-50"
              >
                <LocateFixed className="w-3.5 h-3.5" />
                {backfilling ? "Geocoding…" : "Backfill Coords"}
              </button>
            )}
            <button onClick={() => setEditing("new")} data-testid="new-site-button" className="bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold uppercase tracking-widest px-4 py-2.5">
              + New Site
            </button>
          </div>
        }
      />
      <div className="p-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="sites-list">
          {sites.length === 0 ? (
            <div className="col-span-full text-center text-sm text-gray-500 py-12 bg-white border border-gray-200">No sites yet.</div>
          ) : sites.map((s) => (
            <div key={s.id} className="bg-white border border-gray-200 p-5" data-testid={`site-card-${s.name}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  <span className={`text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 border ${TYPE_COLORS[s.site_type]}`}>
                    {TYPE_LABEL[s.site_type]}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => setEditing(s)} className="p-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-100" data-testid={`edit-site-${s.name}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => remove(s.id)} className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              <div className="font-display font-bold text-gray-900 text-lg">{s.name}</div>
              <div className="text-sm text-gray-600 mt-1">{s.address}</div>
              <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-700">
                <div>{s.contact_name || "—"}</div>
                <div className="font-mono">{s.contact_phone || ""}</div>
                {(s.lat != null && s.lon != null) && (
                  <div className="font-mono text-[10px] text-emerald-700 mt-1" data-testid={`site-coords-${s.name}`}>
                    ◉ {Number(s.lat).toFixed(4)}, {Number(s.lon).toFixed(4)}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {editing && (
        <SiteDialog
          initial={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}
    </div>
  );
}

function SiteDialog({ initial, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: initial?.name || "",
    address: initial?.address || "",
    site_type: initial?.site_type || "construction",
    contact_name: initial?.contact_name || "",
    contact_phone: initial?.contact_phone || "",
    notes: initial?.notes || "",
    lat: initial?.lat ?? "",
    lon: initial?.lon ?? "",
  });
  const [busy, setBusy] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const isEdit = !!initial;

  const autoFillCoords = async () => {
    if (!form.address?.trim()) {
      toast.error("Enter an address first.");
      return;
    }
    setGeocoding(true);
    try {
      // If editing an existing site, use the server endpoint (persists + audits).
      // Otherwise ask the server via a temporary POST route: we don't have one,
      // so instead we create-then-geocode? Simpler: save first then geocode.
      // For a smooth new-site UX, we duplicate the Nominatim call client-side.
      if (isEdit) {
        const { data } = await api.post(`/sites/${initial.id}/geocode`);
        setForm((f) => ({ ...f, lat: data.lat.toFixed(6), lon: data.lon.toFixed(6) }));
        toast.success(`Coordinates filled from address`);
      } else {
        // Client-side geocode via Nominatim so we don't need to save first.
        const r = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(form.address)}&format=json&limit=1`,
          { headers: { "Accept": "application/json" } },
        );
        const results = await r.json();
        if (!results || results.length === 0) {
          toast.error("No match found for that address.");
        } else {
          setForm((f) => ({ ...f, lat: Number(results[0].lat).toFixed(6), lon: Number(results[0].lon).toFixed(6) }));
          toast.success("Coordinates filled from address");
        }
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Geocode failed");
    } finally { setGeocoding(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        lat: form.lat === "" ? null : parseFloat(form.lat),
        lon: form.lon === "" ? null : parseFloat(form.lon),
      };
      if (isEdit) await api.patch(`/sites/${initial.id}`, payload);
      else await api.post("/sites", payload);
      toast.success(isEdit ? "Site updated" : "Site added");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white border border-gray-300 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="border-b border-gray-200 px-5 py-4 flex items-center justify-between sticky top-0 bg-white">
          <div className="font-display font-bold text-xl">{isEdit ? "Edit Site" : "New Site"}</div>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <F label="Name"><input required value={form.name} onChange={(e) => set("name", e.target.value)} className="inp" data-testid="site-name-input" /></F>
          <F label="Address"><input required value={form.address} onChange={(e) => set("address", e.target.value)} className="inp" data-testid="site-address-input" /></F>
          <F label="Type">
            <select value={form.site_type} onChange={(e) => set("site_type", e.target.value)} className="inp" data-testid="site-type-select">
              <option value="construction">Construction</option>
              <option value="dump_yard">Dump Yard</option>
              <option value="fill_site">Fill Site</option>
            </select>
          </F>
          <F label="Contact Name"><input value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} className="inp" data-testid="site-contact-input" /></F>
          <F label="Contact Phone"><input value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} className="inp" data-testid="site-phone-input" /></F>
          <div className="border-t border-gray-200 pt-4">
            <div className="label-caps mb-1">Coordinates (optional)</div>
            <div className="text-[11px] text-gray-500 mb-3">Used for round-trip travel estimates (Haversine distance). Look up via Google Maps → right-click any pin.</div>
            <div className="grid grid-cols-2 gap-3">
              <F label="Latitude"><input type="number" step="0.000001" value={form.lat} onChange={(e) => set("lat", e.target.value)} className="inp font-mono" data-testid="site-lat-input" placeholder="37.7749" /></F>
              <F label="Longitude"><input type="number" step="0.000001" value={form.lon} onChange={(e) => set("lon", e.target.value)} className="inp font-mono" data-testid="site-lon-input" placeholder="-122.4194" /></F>
            </div>
            <button
              type="button"
              onClick={autoFillCoords}
              disabled={geocoding || !form.address?.trim()}
              data-testid="site-autofill-coords"
              className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-semibold text-blue-700 hover:text-blue-900 disabled:opacity-40"
            >
              <LocateFixed className="w-3 h-3" />
              {geocoding ? "Geocoding…" : "Auto-fill from address"}
            </button>
          </div>
          <div className="flex gap-3 pt-3">
            <button type="button" onClick={onClose} className="flex-1 border border-gray-300 py-2.5 text-xs uppercase tracking-widest font-semibold">Cancel</button>
            <button type="submit" disabled={busy} data-testid="submit-site-button" className="flex-1 bg-orange-500 hover:bg-orange-600 text-white py-2.5 text-xs uppercase tracking-widest font-semibold">{isEdit ? "Update" : "Save"}</button>
          </div>
        </form>
        <style>{`.inp { width: 100%; padding: 0.5rem 0.75rem; border: 1px solid #D1D5DB; font-size: 0.875rem; } .inp:focus { outline: none; border-color: #111827; }`}</style>
      </div>
    </div>
  );
}

function F({ label, children }) {
  return <div><label className="label-caps block mb-1.5">{label}</label>{children}</div>;
}
